import caseBeautyStudio from '../assets/cases/case-beauty-studio-vertical.png';
import caseFashionStudio from '../assets/cases/case-fashion-studio-vertical.png';
import caseInterviewStudio from '../assets/cases/case-interview-studio-vertical.png';
import caseKitchenStudio from '../assets/cases/case-kitchen-studio-vertical.png';
import caseLiveCommerce from '../assets/cases/case-live-commerce-vertical.png';
import caseTechLaunch from '../assets/cases/case-tech-launch-vertical.png';
import type {
  DeviceCatalogRecord,
  PackageCatalogRecord,
  PlanCatalogRecord,
} from '../state/types';
import { createProductCatalog } from './productCatalog';

/**
 * Disposable case-library rehearsal fixture. Cases and their plan/package
 * relationships are local demo data; they are not formal API resources,
 * database records, or saved quotations.
 */
export interface CaseShowcaseRecord {
  id: string;
  caseId: string;
  caseCode: string;
  version: 1;
  name: string;
  category: string;
  summary: string;
  targetCustomer: string;
  /** 内容来源，后台案例管理可编辑；缺省视为前端 Fixture。 */
  source?: string;
  /** Business category labels used by the case-library filters. */
  tags: string[];
  planIds: string[];
  heroImage: string;
  gallery: string[];
}

export const DEFAULT_CASE_SOURCE = '前端案例 Fixture';

export const CASE_SOURCE_OPTIONS = [
  DEFAULT_CASE_SOURCE,
  '现场实拍素材',
  '客户提供素材',
];

export interface CaseShowcaseImageOption {
  id: string;
  label: string;
  src: string;
}

/** 后台新增/编辑案例时可选的演示效果图。 */
export const CASE_SHOWCASE_IMAGES: CaseShowcaseImageOption[] = [
  { id: 'live-commerce', label: '木质直播间', src: caseLiveCommerce },
  { id: 'beauty-studio', label: '电商标准间', src: caseBeautyStudio },
  { id: 'interview-studio', label: '访谈演播室', src: caseInterviewStudio },
  { id: 'fashion-studio', label: '服装拍摄间', src: caseFashionStudio },
  { id: 'kitchen-studio', label: '餐饮展示间', src: caseKitchenStudio },
  { id: 'tech-launch', label: '产品发布区', src: caseTechLaunch },
];

export interface CaseShowcaseFixture {
  cases: CaseShowcaseRecord[];
  catalog: {
    devices: DeviceCatalogRecord[];
    packages: PackageCatalogRecord[];
    plans: PlanCatalogRecord[];
  };
}

interface CaseShowcaseSeed {
  name: string;
  caseCode: string;
  image: string;
  tags: string[];
  planIds: string[];
  emptyPlan?: true;
}

const CASE_SHOWCASE_FIXTURE: CaseShowcaseSeed[] = [
  {
    name: '初创团队直播',
    caseCode: 'PLAN-W-01',
    image: caseLiveCommerce,
    tags: ['直播'],
    planIds: ['PLAN-001', 'PLAN-002', 'PLAN-003'],
  },
  {
    name: '品牌电商标准间',
    caseCode: 'PLAN-M-01',
    image: caseBeautyStudio,
    tags: ['电商'],
    planIds: ['PLAN-002', 'PLAN-001', 'PLAN-003'],
  },
  {
    name: '双机访谈直播',
    caseCode: 'PLAN-O-01',
    image: caseInterviewStudio,
    tags: ['访谈', '直播'],
    planIds: ['PLAN-003', 'PLAN-004'],
  },
  {
    name: '企业课程录播间',
    caseCode: 'PLAN-I-01',
    image: caseFashionStudio,
    tags: ['录播', '教育培训'],
    planIds: ['PLAN-004', 'PLAN-003'],
  },
  {
    name: '户外活动直播方案',
    caseCode: 'PLAN-W-01',
    image: caseKitchenStudio,
    tags: ['户外', '直播'],
    planIds: ['PLAN-005', 'PLAN-006'],
  },
  {
    name: '发布会多机位方案',
    caseCode: 'PLAN-U-01',
    image: caseTechLaunch,
    tags: ['发布会'],
    planIds: ['PLAN-006', 'PLAN-005'],
  },
  {
    name: '短视频种草拍摄',
    caseCode: 'CASE-VIDEO-01',
    image: caseFashionStudio,
    tags: ['短视频'],
    planIds: ['PLAN-005', 'PLAN-001'],
  },
  {
    name: '企业培训录播教室',
    caseCode: 'CASE-TRAINING-01',
    image: caseInterviewStudio,
    tags: ['教育培训', '录播'],
    planIds: ['PLAN-004', 'PLAN-001'],
  },
  {
    name: '企业会议远程直播',
    caseCode: 'CASE-MEETING-01',
    image: caseTechLaunch,
    tags: ['企业会议', '直播'],
    planIds: ['PLAN-003', 'PLAN-006'],
  },
  {
    name: '双人播客访谈间',
    caseCode: 'CASE-PODCAST-01',
    image: caseInterviewStudio,
    tags: ['播客', '访谈'],
    planIds: ['PLAN-006', 'PLAN-003'],
  },
  {
    name: '新品产品演示直播',
    caseCode: 'CASE-DEMO-01',
    image: caseLiveCommerce,
    tags: ['产品演示', '直播'],
    planIds: ['PLAN-001', 'PLAN-003'],
  },
  {
    name: '品牌宣传内容制作',
    caseCode: 'CASE-BRAND-01',
    image: caseBeautyStudio,
    tags: ['品牌宣传', '电商'],
    planIds: ['PLAN-002', 'PLAN-005'],
  },
  {
    name: '未关联方案演练案例',
    caseCode: 'CASE-UNLINKED-01',
    image: caseKitchenStudio,
    tags: ['品牌宣传'],
    planIds: [],
  },
  {
    name: '空设备清单演练案例',
    caseCode: 'CASE-EMPTY-PLAN-01',
    image: caseTechLaunch,
    tags: ['产品演示'],
    planIds: ['PLAN-007'],
    emptyPlan: true,
  },
];

function createPlanOnePackageRelations(
  packageId: string,
  devices: DeviceCatalogRecord[],
  startIndex: number,
  count: number,
  priceOffset: number,
) {
  return devices
    .slice(startIndex, startIndex + count)
    .map((device, sortOrder) => {
      const priceFactor = [0.72, 0.78, 0.84, 0.9][
        (sortOrder + priceOffset) % 4
      ];
      const packageItemPrice =
        device.rentalPrice == null
          ? null
          : Math.round(device.rentalPrice * priceFactor * 100) / 100;

      return {
        relationId: `CASE-SHOWCASE-${packageId}-${String(sortOrder + 1).padStart(2, '0')}`,
        packageId,
        deviceId: device.id,
        quantity: sortOrder % 11 === 0 ? 2 : sortOrder % 7 === 0 ? 3 : 1,
        sortOrder,
        packageItemPrice,
      };
    });
}

export function createCaseShowcaseCatalog(): CaseShowcaseRecord[] {
  return CASE_SHOWCASE_FIXTURE.map((fixture, index) => {
    const caseId = `CASE-${String(index + 1).padStart(3, '0')}`;
    const isUnlinked = fixture.planIds.length === 0;

    return {
      id: `${caseId}:V1`,
      caseId,
      caseCode: fixture.caseCode,
      version: 1,
      name: fixture.name,
      category: fixture.tags[0] ?? '未分类',
      summary: isUnlinked
        ? '演练样例：未关联方案，用于检查案例无方案时的空状态。'
        : fixture.emptyPlan
          ? '演练样例：已关联无套餐方案，设备清单为空，用于检查空清单提示。'
          : `${fixture.planIds.length} 个可选方案；可进入设备清单并生成报价演练草稿。`,
      targetCustomer: fixture.name,
      tags: [...fixture.tags],
      planIds: [...fixture.planIds],
      heroImage: fixture.image,
      gallery: [fixture.image],
    };
  });
}

export function createCaseShowcaseFixture(): CaseShowcaseFixture {
  const sourceCatalog = createProductCatalog();
  const devices = structuredClone(sourceCatalog.devices);
  const packages = structuredClone(sourceCatalog.packages);
  const plans = structuredClone(sourceCatalog.plans);
  plans.push({
    id: 'PLAN-007',
    name: '空套餐报价演练方案',
    discount: 100,
    sortOrder: plans.length,
    image: caseTechLaunch,
    cases: ['空套餐报价演练案例图'],
  });

  const packageRelationSeeds = [
    { packageId: 'PKG-001', startIndex: 0, count: 10, priceOffset: 0 },
    { packageId: 'PKG-007', startIndex: 10, count: 23, priceOffset: 1 },
  ];

  for (const seed of packageRelationSeeds) {
    const packageRecord = packages.find(
      (record) => record.id === seed.packageId,
    );
    if (packageRecord) {
      packageRecord.productLinks = createPlanOnePackageRelations(
        seed.packageId,
        devices,
        seed.startIndex,
        seed.count,
        seed.priceOffset,
      );
    }
  }

  return {
    cases: createCaseShowcaseCatalog(),
    catalog: { devices, packages, plans },
  };
}
